import type { IGeolocationInfo } from '../models/geolocation-info';
import type { IGoToLocalInfo } from '../models/go-to-local-info';
import type { RawServerResponse } from '../models/raw-server-response';
import type { UXContext } from '../models/ux-context';
import { fetch } from './fetch';

export interface IFetchCustomerExperienceContextOptions {
    serverUrl?: URL;
}

export interface IFetchGoToLocalOptions {
    index?: string;
    pageRefId: string;
    serverUrl?: URL;
    linkType?: string;
    preselection?: string;
    locale?: string;
}

export const defaultServerUrl = new URL('https://dc-ncj-portal.dev.dxf.bosch.tech/customer-experience/my-context');
let serverUrl = defaultServerUrl;

export const defaultGTLTargetsUrl = new URL(
    'https://dc-ncj-portal.dev.dxf.bosch.tech/customer-experience/dc-caas/goto-local-targets',
);

export const defaultGTLSessionUrl = new URL(
    'https://dc-ncj-portal.dev.dxf.bosch.tech/customer-experience/dc-caas/goto-local-session',
);

export function getServerUrl(): URL {
    return serverUrl;
}

export function setServerUrl(url: URL) {
    serverUrl = url;
}

/**
 * Get the customer experience object from the server, parse the response and update the modal view state
 */
export async function fetchCustomerExperienceContext(
    options?: IFetchCustomerExperienceContextOptions,
): Promise<UXContext & RawServerResponse> {
    let customerExperienceObj: UXContext & RawServerResponse;

    {
        const url = new URL(options?.serverUrl?.href ?? serverUrl.href);

        url.search = window.location.search;

        const call = await fetch(url.href, {
            headers: {
                accept: 'application/json',
                'x-alt-referer': window.location.href.toString(),
            },
        });

        if (call.status >= 400) {
            throw new Error(await call.text());
        }

        customerExperienceObj = server2UXContext(await call.text());
    }

    {
        const locationsInfo = await fetchGeolocationInfo(options);
        customerExperienceObj.references = locationsInfo.references;
        customerExperienceObj.targetLocation = locationsInfo.target_location ?? undefined;
        customerExperienceObj.targetRegion = locationsInfo.target_region ?? undefined;
    }

    return customerExperienceObj;
}

export async function fetchGeolocationInfo(
    options?: IFetchCustomerExperienceContextOptions,
): Promise<IGeolocationInfo> {
    const url = new URL(options?.serverUrl?.href ?? defaultGTLTargetsUrl.href);
    url.pathname = '/customer-experience/dc-caas/geolocation';

    if (url.host.includes('.dev.dxf')) {
        url.searchParams.set('index', 'rb-dc-xc-en'); //rb-dc-com-en
    }

    const call = await fetch(url.href, {
        headers: {
            accept: 'application/json',
            'x-alt-referer': window.location.href.toString(),
        },
    });

    return await call.json();
}

export async function fetchGoToLocalInfo(options: IFetchGoToLocalOptions): Promise<IGoToLocalInfo> {
    const url = new URL(options.serverUrl?.href ?? defaultGTLTargetsUrl.href);

    if (options.index) {
        url.searchParams.set('index', options.index);
    }

    if (options.linkType) {
        url.searchParams.set('linkType', options.linkType);
    }

    if (options.preselection) {
        url.searchParams.set('preselection', options.preselection);
    }

    if (options.locale) {
        url.searchParams.set('locale', options.locale);
    }

    url.searchParams.set('globalPageIds', options.pageRefId);

    const call = await fetch(url.href, {
        headers: {
            accept: 'application/json',
            'x-alt-referer': window.location.href.toString(),
        },
        credentials: 'include',
    });

    return await call.json();
}

export async function createSession(
    sessionUrl: string,
    languageCode: string,
    countryCode: string,
    isChecked: boolean,
): Promise<void> {
    const url = new URL(sessionUrl ?? defaultGTLSessionUrl);

    if (languageCode) {
        url.searchParams.set('language', languageCode.toLowerCase());
    }

    if (countryCode) {
        url.searchParams.set('country', countryCode.toLowerCase());
    }

    url.searchParams.set('checked', `${isChecked}`);

    await fetch(url.href, {
        credentials: 'include',
        mode: 'no-cors',
    });
}

/**
 * Deserialize server response containing a customer experience object
 * @param serverResponse
 * @protected
 */
const server2UXContext = (serverResponse: string): UXContext & RawServerResponse => {
    // biome-ignore lint/suspicious/noExplicitAny: todo
    const rawObj: any = JSON.parse(serverResponse);
    return {
        isBlocked: rawObj.isBlocked ?? false,
        systemActions: JSON.parse(rawObj.systemActions ?? '[]'),
        chatDestinationCountry: rawObj.chatDestinationCountry ?? '',
        countrySwitchIsNeeded: rawObj.countrySwitchIsNeeded ?? false,
        countrySwitchTarget: rawObj.countrySwitchTarget ?? '',
        queryParams: rawObj.queryParams ?? '',
        refererPath: rawObj.refererPath ?? window.location.pathname,
        references: {},
        switchCountryModalDescription: rawObj.switchCountryModalDescription ?? 'Please select a site:',
        switchCountryModalHomeCountryButtonEventLabel:
            rawObj.switchCountryModalHomeCountryButtonEventLabel ?? 'NATIONAL SITE',
        switchCountryModalHomeCountryButtonLabel: rawObj.switchCountryModalHomeCountryButtonLabel ?? 'NATIONAL SITE',
        switchCountryModalInternationalButtonLabel:
            rawObj.switchCountryModalInternationalButtonLabel ?? 'INTERNATIONAL SITE',
        switchCountryModalInternationalButtonEventLabel:
            rawObj.switchCountryModalInternationalButtonEventLabel ?? 'International',
        switchCountryModalTitle: rawObj.switchCountryModalTitle ?? 'Do you want to switch to your national site?',
        rawResponseObject: rawObj,
        rawResponseString: serverResponse,
    };
};

export const _ = {
    server2UXContext,
};
